import { createAsyncThunk } from '@reduxjs/toolkit';
import { EVENTS_NAMES_MAP } from '../events-name';
import { trackKlaviyo } from '../utils/klaviyo.util';
import { selectedActiveUser } from '@castlery/modules-user-domain';
import { RootState } from '@castlery/shared-redux-store';
import {
  getBreadcrumbNames,
  TRACKING_MSGS_MAP,
  getProductImageUrl,
  getProductUrl,
  getItemsForKlaviyo,
  getCheckoutUrl,
} from '../helpers';
import { selectProduct, selectVariant } from '@castlery/modules-product-domain';
import { Image, LineItem } from '@castlery/types';
import { logger } from '@castlery/observability';

/**
 * KL event : identify
 */
export const trackKlaviyoIdentifyEvent = createAsyncThunk(
  'tracking/trackKlaviyoIdentifyEvent',
  async (payload: { email: string; firstname: string; lastname: string }, { getState, fulfillWithValue }) => {
    if (!payload.email) {
      logger.warn('~file: klaviyo-events.trigger.ts:16: customer is not found');
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    trackKlaviyo(EVENTS_NAMES_MAP.KL_IDENTIFY, {
      $email: payload.email,
      $first_name: payload.firstname,
      $last_name: payload.lastname,
    });

    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

/**
 * KL event : Viewed Product
 */
export const trackKlaviyoViewedProductEvent = createAsyncThunk(
  'tracking/trackKlaviyoViewedProductEvent',
  async (_, { getState, fulfillWithValue }) => {
    const rootState = getState() as RootState;
    const product = selectProduct(rootState);
    const variant = selectVariant(rootState);
    if (!product || !variant) {
      logger.warn('~file: klaviyo-events.trigger.ts:36: product or variant is not found');
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    const [pageName, subPageName] = getBreadcrumbNames(product.taxons);

    trackKlaviyo(EVENTS_NAMES_MAP.KL_VIEWED_PRODUCT, {
      ProductName: variant.product_name,
      ProductID: variant.id,
      SKU: variant.sku,
      Categories: [pageName, subPageName],
      ImageURL: getProductImageUrl(variant.images as Image[]),
      URL: getProductUrl(variant as any, product.slug),
      Brand: 'Castlery',
      Price: +variant.price,
      CompareAtPrice: +variant.list_price,
    });
    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

/**
 * KL event : Added to Cart
 */
export const trackKlaviyoAddedToCartEvent = createAsyncThunk(
  'tracking/trackKlaviyoAddedToCartEvent',
  async (
    payload: {
      targetVariantSku: LineItem['variant']['sku'];
      qtyIncrements: number;
      cartLineItems: LineItem[];
      itemTotal: string;
    },
    { fulfillWithValue }
  ) => {
    if (!payload) {
      console.error('~file: klaviyo-events.trigger.ts:83: payload is not found');
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    const { targetVariantSku } = payload;

    const targetItem = payload.cartLineItems.find((item) => item.variant.sku === targetVariantSku);
    if (!targetItem) {
      console.error('~file: klaviyo-events.trigger.ts:83: targetItem is not found');
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }

    const { variant } = targetItem;

    const lineItems = payload.cartLineItems;
    const items = getItemsForKlaviyo(lineItems);

    const params = {
      $value: +payload.itemTotal,
      ItemNames: items.map((item) => item.ProductName),
      CheckoutURL: getCheckoutUrl(),
      AddedItemProductName: variant.product_name,
      AddedItemProductID: variant.id,
      AddedItemSKU: variant.sku,
      AddedItemPrice: +variant.price,
      AddedItemQuantity: payload.qtyIncrements,
      AddedItemCategories: getBreadcrumbNames(variant.product_taxons),
      ProductURL: getProductUrl(targetItem?.variant, variant.product_slug),
      ImageURL: getProductImageUrl(variant.images),
      Items: items,
    };
    trackKlaviyo(EVENTS_NAMES_MAP.KL_ADDED_TO_CART, params);
    return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
  }
);

/**
 * KL event : Started Checkout : todo  @abby
 */
